/**
 * KBSearchPanel — semantic search input + ranked results.
 * FR-042: validates RAG quality via admin debug search.
 * WCAG 2.1 AA: labelled input, live region for results, focus management.
 */
import { useRef, useState } from 'react';
import { Search, FileText, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { DocType } from '@/hooks/useKBDocuments';
import { useKBSearch } from '@/hooks/useKBDocuments';

const JURISDICTIONS = ['EU', 'PT', 'DE', 'FR', 'ES', 'NL', 'PL'];

interface KBSearchPanelProps {
  className?: string;
}

function SimilarityBar({ score }: { score: number }) {
  const pct = Math.round(score * 100);
  return (
    <div className="flex items-center gap-2 mt-1">
      <div
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Similarity score ${pct}%`}
        className="h-1.5 w-24 overflow-hidden rounded-full bg-muted"
      >
        <div
          className={cn(
            'h-full rounded-full',
            pct >= 80 ? 'bg-green-500' : pct >= 60 ? 'bg-yellow-500' : 'bg-muted-foreground',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground">{pct}% match</span>
    </div>
  );
}

const DOC_TYPE_LABELS: Record<DocType, string> = {
  legislation: 'Legislation',
  regulation: 'Regulation',
  directive: 'Directive',
  guideline: 'Guideline',
  internal_policy: 'Internal Policy',
};

export function KBSearchPanel({ className }: KBSearchPanelProps) {
  const [query, setQuery] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const { mutate: search, data, isPending, error } = useKBSearch();

  const handleSearch = () => {
    const trimmed = query.trim();
    if (!trimmed) {
      inputRef.current?.focus();
      return;
    }
    search({ query: trimmed, jurisdiction: jurisdiction || undefined, top_k: 5 });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <section aria-labelledby="kb-search-heading" className={cn('space-y-4', className)}>
      <h2 id="kb-search-heading" className="text-sm font-semibold text-foreground">
        Semantic Search (Admin Debug)
      </h2>

      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <label htmlFor="kb-search-input" className="sr-only">
            Search legislation
          </label>
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <input
            id="kb-search-input"
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => { setQuery(e.target.value); }}
            onKeyDown={handleKeyDown}
            placeholder="Search legislation… e.g., 'data processing obligations'"
            className={cn(
              'w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            )}
            aria-describedby={error ? 'kb-search-error' : undefined}
          />
        </div>

        <div>
          <label htmlFor="kb-search-jurisdiction" className="sr-only">
            Filter by jurisdiction
          </label>
          <select
            id="kb-search-jurisdiction"
            value={jurisdiction}
            onChange={(e) => { setJurisdiction(e.target.value); }}
            className={cn(
              'h-9 rounded-md border border-input bg-background px-3 text-sm',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            )}
          >
            <option value="">All jurisdictions</option>
            {JURISDICTIONS.map((j) => (
              <option key={j} value={j}>{j}</option>
            ))}
          </select>
        </div>

        <Button onClick={handleSearch} disabled={isPending} aria-busy={isPending}>
          {isPending ? 'Searching…' : 'Search'}
        </Button>
      </div>

      {error && (
        <p id="kb-search-error" role="alert" className="flex items-center gap-1.5 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          Search failed. Please try again.
        </p>
      )}

      <div
        ref={resultsRef}
        aria-live="polite"
        aria-atomic="false"
        className="space-y-3"
      >
        {data && data.results.length === 0 && (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No matching documents found. Try broadening your search terms.
          </p>
        )}

        {data && data.results.length > 0 && (
          <ol className="space-y-3" aria-label={`${data.total} search results`}>
            {data.results.map((result, idx) => (
              <li
                key={`${result.document_id}-${result.chunk_index}`}
                className="rounded-lg border border-border bg-card p-4"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                    <span className="text-sm font-medium text-foreground truncate">{result.title}</span>
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground">#{idx + 1}</span>
                </div>
                <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
                  <span>{result.source}</span>
                  <span aria-hidden="true">&middot;</span>
                  <span>{DOC_TYPE_LABELS[result.doc_type]}</span>
                  {result.jurisdiction && (
                    <>
                      <span aria-hidden="true">&middot;</span>
                      <span>{result.jurisdiction}</span>
                    </>
                  )}
                  <span aria-hidden="true">&middot;</span>
                  <span>v{result.version}</span>
                  <span aria-hidden="true">&middot;</span>
                  <span>chunk {result.chunk_index}</span>
                </div>
                <SimilarityBar score={result.similarity_score} />
                <blockquote className="mt-2 rounded bg-muted/50 px-3 py-2 text-xs text-muted-foreground italic line-clamp-3">
                  {result.chunk_text}
                </blockquote>
              </li>
            ))}
          </ol>
        )}

        {!data && !isPending && (
          <p className="text-sm text-muted-foreground py-4 text-center">
            Enter a query to search legislation.
          </p>
        )}
      </div>
    </section>
  );
}
